import { Input, SuggestionsList } from '@pb/components'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import styles from './InformaticaAddressVerification.module.scss'
import cn from 'classnames'
import {
  InformaticaResults,
  getInformaticaSuggestedAddresses,
} from 'helpers/getInformaticaSuggestedAddresses'
import { InformaticaAddressVerificationProps } from './types'
import { getInformaticaAddressToken } from 'helpers/getInformaticaAddressToken'
import { CLICK_CO_ADDRESS_AUTOCOMPLETE_FILL } from 'constants/optimizely'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { useFormContext } from 'react-hook-form'

export const InformaticaAddressVerification: React.VFC<InformaticaAddressVerificationProps> = ({
  className,
  label,
  placeholder,
  onBlur,
  onFocus,
  country,
  minSearchCharacter = 3,
  fetchAddressDelay = 300,
  value: inputValue,
  ...props
}) => {
  const [authToken, setAuthToken] = useState<string>('')
  const [jobToken, setJobToken] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)
  const [suggestedAddreses, setSuggestedAddresses] = useState<
    { text: string; id: string }[] | never[]
  >([])
  const [informaticaAddresses, setInformaticaAddresses] = useState<InformaticaResults[] | never[]>(
    [],
  )
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false)

  const { setValue } = useFormContext()
  const userHasMinimumSearchLength = inputValue && inputValue.length >= minSearchCharacter
  const shouldShowAddressSuggestions =
    isFocused && suggestedAddreses && !!userHasMinimumSearchLength

  const formatAddressOutput = (string: string): string => {
    if (!string) return ''
    return string
      .split(' ')
      .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
      .join(' ')
  }

  const formatHouseNumberOutput = (houseNumberInput: string): string => {
    const invalidHouseNumberLimiters = [';', '..']

    if (!houseNumberInput) return ''
    if (invalidHouseNumberLimiters.some((s) => houseNumberInput.includes(s))) {
      const regex = /(\d+)(?:;|\.\.)/
      const firstHouseNumber = houseNumberInput.match(regex)?.[1]
      return firstHouseNumber || ''
    }
    return houseNumberInput
  }

  const normalizeAddressSuggestion = (
    addressResponse: InformaticaResults[],
  ): { text: string; id: string }[] => {
    return addressResponse?.map((informaticaAddress, index) => {
      const streetName = informaticaAddress?.Variants?.[0]?.AddressElements?.Street?.[0]?.Value
      const houseNumber =
        informaticaAddress?.Variants?.[0]?.AddressElements?.HouseNumber?.[0]?.Value
      const normalizedHouseNumber = houseNumber ? formatHouseNumberOutput(houseNumber) : ''
      const postalCode = informaticaAddress?.Variants?.[0]?.AddressElements?.PostalCode?.[0]?.Value
      const town = informaticaAddress?.Variants?.[0]?.AddressElements?.Locality?.[0]?.Value
      const suggestedAddressText = `${streetName ? formatAddressOutput(streetName) : ''}${
        normalizedHouseNumber ? ` ${normalizedHouseNumber}` : ''
      }${streetName ? ', ' : ''}${town ? `${formatAddressOutput(town)}` : ''}${town ? ', ' : ''}${
        postalCode ? `${formatAddressOutput(postalCode)}` : ''
      }`
      return { text: suggestedAddressText, id: index.toString() }
    })
  }

  const fetchAddresses = async (): Promise<void> => {
    const userInput = inputValue as string
    const fetchedAddressesResponse = await getInformaticaSuggestedAddresses({
      authToken,
      userInput,
      country,
      jobToken,
    })
    const addressResults = fetchedAddressesResponse?.Response?.[0]?.Results
    const normalizedAddresses = normalizeAddressSuggestion(addressResults)
    const hasResults = normalizedAddresses && normalizedAddresses.length > 0
    if (hasResults) {
      setJobToken(fetchedAddressesResponse.JobToken)
      setInformaticaAddresses(addressResults)
      setSuggestedAddresses(normalizedAddresses)
      setShouldShowSuggestions(true)
    }
  }

  const handleBlur = (fieldValue: React.FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false)
    if (onBlur) {
      onBlur(fieldValue)
    }
  }

  const handleFocus = (): void => {
    setIsFocused(true)
    if (onFocus) {
      onFocus()
    }
  }

  const normalizeAddressToFieldValues = ({
    id,
  }: {
    id: string
  }): {
    line1Value: string
    line2Value?: string
    postalCodeValue: string
    townValue: string
    provinceValue?: string
  } => {
    const index = parseInt(id, 10)
    const addressToFill = informaticaAddresses?.[index]?.Variants?.[0]?.AddressElements
    const streetValue = addressToFill?.Street?.[0]?.Value || ''
    const houseNumberValue = formatHouseNumberOutput(addressToFill?.HouseNumber?.[0]?.Value || '')
    const line1Value = formatAddressOutput(
      `${streetValue}${houseNumberValue ? ' ' : ''}${houseNumberValue}`,
    )
    const townValue = formatAddressOutput(addressToFill?.Locality?.[0]?.Value || '')
    const postalCodeValue = addressToFill?.PostalCode?.[0]?.Value || ''
    const buildingValue = addressToFill?.Building?.[0]?.Value || ''
    const subBuildingValue = formatHouseNumberOutput(addressToFill?.SubBuilding?.[0]?.Value || '')
    const line2Value = formatAddressOutput(
      `${buildingValue}${subBuildingValue ? ' ' : ''}${subBuildingValue}`,
    )
    const provinceValue = formatAddressOutput(
      addressToFill?.AdministrativeDivision?.[0]?.Value || '',
    )

    return { line1Value, line2Value, postalCodeValue, townValue, provinceValue }
  }

  const handleConfirm = ({ id }): void => {
    setShouldShowSuggestions(false)
    trackOptimizelyEvent(CLICK_CO_ADDRESS_AUTOCOMPLETE_FILL)
    const { line1Value, line2Value, postalCodeValue, townValue, provinceValue } =
      normalizeAddressToFieldValues({ id })
    setValue('line1', line1Value, { shouldValidate: true, shouldDirty: true })
    if (line2Value) setValue('line2', line2Value, { shouldValidate: true, shouldDirty: true })
    setValue('town', townValue, { shouldValidate: true, shouldDirty: true })
    setValue('postalCode', postalCodeValue, { shouldValidate: true, shouldDirty: true })
    setValue('province', provinceValue, { shouldValidate: true, shouldDirty: true })
  }

  useLayoutEffect(() => {
    const fetchToken = async (): Promise<void> => {
      const fetchedToken = await getInformaticaAddressToken()
      setAuthToken(fetchedToken.authToken)
    }
    if (isFocused) {
      fetchToken()
    }
  }, [isFocused])

  // Only search for addresses once user has stopped typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const shouldGetSuggestions = authToken && isFocused && userHasMinimumSearchLength
      if (shouldGetSuggestions) fetchAddresses()
    }, fetchAddressDelay)

    return () => clearTimeout(delayDebounceFn)
  }, [inputValue])

  return (
    <div className={cn([styles.container, className])}>
      <Input
        className={styles.input}
        role="combobox"
        type="text"
        name="addressAutocomplete"
        aria-autocomplete="list"
        aria-haspopup="true"
        aria-owns="addressSuggestions"
        aria-expanded={shouldShowAddressSuggestions}
        placeholder={placeholder}
        onBlur={handleBlur}
        onFocus={handleFocus}
        label={label}
        shouldDisableValidation
        shouldShowValidationIcon={false}
        autoComplete="off"
        value={inputValue}
        {...props}
      />
      {shouldShowSuggestions && suggestedAddreses && (
        <SuggestionsList
          className={styles.suggestionsList}
          suggestions={suggestedAddreses}
          onSuggestionConfirm={handleConfirm}
          onClose={() => setShouldShowSuggestions(false)}
        />
      )}
    </div>
  )
}
